import { useEffect, useRef, useState } from "react";
import { apiGet } from "../lib/api";

export type RuianSuggestion = {
  label: string;
  street: string;
  houseNumber: string;
  city: string;
  postalCode: string;
  ruianId: number | null;
  latitude: number | null;
  longitude: number | null;
};

type Props = {
  value: string;
  onChange: (q: string) => void;
  onSelect: (suggestion: RuianSuggestion) => void;
  placeholder?: string;
  id?: string;
};

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, id }: Props) {
  const [suggestions, setSuggestions] = useState<RuianSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef(false);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (value.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      try {
        const data = await apiGet<{ items: RuianSuggestion[] }>(
          `/api/ruian/search?q=${encodeURIComponent(value)}`,
        );
        setSuggestions(data.items);
        setOpen(true);
      } catch (err) {
        console.error("RUIAN search failed", err);
      }
    }, 250);
  }, [value]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="autocomplete" ref={containerRef}>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder ?? "Začněte psát adresu..."}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="autocomplete-list">
          {suggestions.map((s, i) => (
            <li
              key={`${s.label}-${i}`}
              onClick={() => {
                justSelectedRef.current = true;
                onSelect(s);
                setOpen(false);
              }}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
